import { useState } from 'react';
import JSZip from 'jszip';

/**
 * Хук для работы с SVG-ассетами: загрузка, копирование, скачивание
 */
const useSVGHandler = (assets, accessToken, isLoggedIn, isPurchased = false, isSubscriptionActive = false, debug = false, archiveName) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [cachedAssets, setCachedAssets] = useState(null);

  /**
   * Получает данные ассетов с сервера
   */
  const fetchAssets = async () => {
    // Возвращаем кэшированные данные, если они уже есть
    if (cachedAssets) {
      return cachedAssets;
    }
    
    // Проверяем, являются ли все ассеты бесплатными из коллекции "basic"
    const allFreeBasic = assets.every(asset => 
      asset.is_free === true || asset.collection === 'basic'
    );

    // Для платных ассетов требуется либо покупка, либо активная подписка
    const hasPremiumAccess = isPurchased || isSubscriptionActive;

    // Проверка доступа для платных ассетов
    if (!allFreeBasic && (!hasPremiumAccess || !isLoggedIn || !accessToken)) {
      throw new Error(
        !isLoggedIn || !accessToken 
          ? 'You must be logged in to download SVGs.' 
          : 'You must purchase this icon to download the SVG.'
      );
    }

    if (!assets || assets.length === 0) {
      throw new Error('No SVG assets available.');
    }

    // Проверка обязательных параметров
    const missingAssets = assets.filter(
      (asset) => !asset.slug || !asset.collection || !asset.format
    );

    if (missingAssets.length > 0) {
      throw new Error('Some assets are missing required parameters.');
    }

    const requestData = {
      assets: assets.map((asset) => ({
        slug: asset.slug,
        collection: asset.collection,
        media_type: asset.media_type || 'vector',
        is_free: asset.is_free !== undefined ? asset.is_free : false,
        format: asset.format || ['svg', 'png'],
      })),
    };

    const headers = {
      'Content-Type': 'application/json',
    };

    // Добавляем токен авторизации только если нужно
    if (!allFreeBasic && accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    try {
      const response = await fetch('https://davoodacom-download.willsavetheworld.workers.dev/', {
        method: 'POST',
        headers,
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} ${errorText}`);
      }

      const responseData = await response.json();
      if (!responseData || !responseData.images) {
        throw new Error('Invalid response from server.');
      }

      const images = responseData.images;
      if (images.length === 0) {
        throw new Error('No images returned from the server.');
      }

      // Кэшируем полученные данные
      setCachedAssets(images);
      
      if (debug) {
        console.log('[useSVGHandler] Fetched assets:', images);
      }

      return images;
    } catch (error) {
      console.error('[useSVGHandler] Fetch error:', error);
      throw error;
    }
  };

  /**
   * Загружает SVG-файлы на устройство пользователя
   */
  const downloadSVGs = async () => {
    setIsDownloading(true);
    setErrorMessage('');
    
    try {
      const images = await fetchAssets();

      if (images.length === 1) {
        // Одиночный ассет
        const image = images[0];
        const { slug, formats, error } = image;
        
        if (error) {
          throw new Error(`Error for ${slug}: ${error}`);
        }
        
        if (!formats.svg) {
          throw new Error(`No SVG format available for ${slug}.`);
        }

        // Скачиваем одиночный SVG
        const blob = new Blob([formats.svg], { type: 'image/svg+xml' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${slug}.svg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

      } else {
        // Несколько ассетов: упаковываем в zip
        const zip = new JSZip();

        for (const image of images) {
          const { slug, formats, error } = image;
          
          if (error) {
            throw new Error(`Error for ${slug}: ${error}`);
          }
          
          if (!formats.svg) {
            throw new Error(`No SVG format available for ${slug}.`);
          }
          
          zip.file(`${slug}.svg`, formats.svg);
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const defaultArchiveName = archiveName || `davoodacom-svgs.zip`;

        // Скачиваем zip-архив
        const url = window.URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = defaultArchiveName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
      
      // Возвращаем true в случае успеха
      return true;
      
    } catch (error) {
      console.error('[useSVGHandler] Download error:', error);
      setErrorMessage(error.message);
      // Пробрасываем ошибку выше
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Копирует SVG в буфер обмена
   * Основная логика вынесена в компонент CopySVGButton
   */
  const copySVGs = async () => {
    setIsDownloading(true);
    setErrorMessage('');

    try {
      const images = await fetchAssets();

      if (images.length === 1) {
        const image = images[0];
        const { formats, error } = image;
        
        if (error) {
          throw new Error(`Error: ${error}`);
        }
        
        if (!formats.svg) {
          throw new Error('No SVG format available.');
        }

        // Возвращаем SVG данные для копирования в компоненте
        return {
          svgString: formats.svg,
          blob: new Blob([formats.svg], { type: 'image/svg+xml' })
        };
      } else {
        throw new Error('Copying multiple SVGs is not implemented.');
      }
    } catch (error) {
      console.error('[useSVGHandler] Copy error:', error);
      setErrorMessage(error.message);
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Очищает кэш ассетов
   */
  const clearCache = () => {
    setCachedAssets(null);
  };

  return {
    isDownloading,
    setIsDownloading,
    errorMessage,
    setErrorMessage,
    downloadSVGs,
    copySVGs,
    fetchAssets,
    clearCache
  };
};

export default useSVGHandler;